import {
  activeSlideIsOutdated,
  loadSlide,
  type PayloadLoadSlide,
  loadVisualizationSlideSuccess,
  toggleLegend,
  visualizationSlideStateLoaded,
} from './actions';
import { filter, tap, withLatestFrom } from 'rxjs/operators';
import { loadSlideState } from './slideLoader';
import {
  dispatchAction,
  collectRoutines,
  routine,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import { SlideTypes, ViewIds } from '@ardoq/api-types';
import { metamodelNavigation$ } from 'metamodel/navigation/metamodelNavigation$';
import { metamodels$ } from 'streams/metamodels/metamodels$';
import { VisualizationSlide } from '@ardoq/api-types';
import {
  selectMetamodelForSlide,
  setMetamodelContextSettings,
} from 'metamodel/navigation/actions';
import { slideLegend$ } from 'presentation/streams/slideLegend$';
import { isSlideLegendActive } from 'presentation/streams/slideLegend';
import { updateViewSettings, viewSettingsConsts } from '@ardoq/view-settings';
import { slideOperations } from 'streams/slides/slideOperations';
import { toSlideViewSettings } from './slideViewSettings';
import { slides$ } from 'streams/slides/slides$';
import { loadedPresentation$ } from 'presentation/streams/loadedPresentation$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { logError } from '@ardoq/logging';
import { toRenderedViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import { context$ } from 'streams/context/context$';
import { slideInterface } from 'modelInterface/presentations/slideInterface';

type PayloadLoadVisualizationSlide = PayloadLoadSlide & { viewId: ViewIds };

const isLoadVisualizationSlide = (
  payload: PayloadLoadSlide
): payload is PayloadLoadVisualizationSlide =>
  payload.type === SlideTypes.VISUALIZATION && payload.viewId !== undefined;

const reactToLoadVisualizationSlide = routine(
  ofType(loadSlide),
  extractPayload(),
  filter(isLoadVisualizationSlide),
  withLatestFrom(loadedPresentation$, slides$, context$, loadedGraph$),
  tap(
    async ([
      { id, viewId, viewState },
      loadedPresentation,
      slideState,
      { workspaceIds },
      { isViewpointMode: wasInViewpointMode },
    ]) => {
      dispatchAction(activeSlideIsOutdated());
      // hackish but we've filtered above, so we know its a viz slide
      const slide = slideState.byId[id] as VisualizationSlide;
      const presentation = loadedPresentation.presentation;
      if (presentation && slide) {
        await loadSlideState(
          presentation,
          slide,
          workspaceIds,
          wasInViewpointMode
        );
      } else {
        logError(Error('Presentation or slide were not found'));
      }

      dispatchAction(
        visualizationSlideStateLoaded({
          slideId: id,
          viewId: toRenderedViewpointViewId(viewId),
          viewState,
          isViewpointMode: Boolean(
            slide && slideOperations.isViewpointSlide(slide)
          ),
        })
      );
    }
  )
);

const applyLoadedSlideToView = routine(
  ofType(visualizationSlideStateLoaded),
  extractPayload(),
  withLatestFrom(slideLegend$),
  tap(([{ slideId, viewId, viewState, isViewpointMode }, slideLegendState]) => {
    dispatchAction(
      updateViewSettings({
        viewId,
        settings: toSlideViewSettings({
          viewId,
          viewState,
          isViewpointMode,
          isLegendActive: isSlideLegendActive(slideLegendState, slideId),
        }),
        persistent: false,
      })
    );

    dispatchAction(loadVisualizationSlideSuccess());
  })
);

const reactToLoadMetamodelSlide = routine(
  ofType(loadSlide),
  extractPayload(),
  filter(({ id }) => slideInterface.isMetamodelSlide(id)),
  withLatestFrom(metamodels$, metamodelNavigation$),
  tap(([, , { context }]) => {
    if (!context.settings.isolatedNavigation) {
      dispatchAction(setMetamodelContextSettings({ isolatedNavigation: true }));
    }
  }),
  tap(([{ id, metamodelId }, metamodels]) => {
    const metamodel = metamodels.find(
      metamodel => metamodel._id === metamodelId
    );
    if (metamodel) {
      dispatchAction(selectMetamodelForSlide(metamodel));
      return;
    }
    logError(Error('The metamodel of a metamodel slide was not found'), null, {
      slideId: id,
      metamodelId,
    });
  })
);

const applyToggledLegendToView = routine(
  ofType(toggleLegend),
  extractPayload(),
  tap(({ viewId, legendActive }) => {
    dispatchAction(
      updateViewSettings({
        viewId,
        settings: { [viewSettingsConsts.IS_LEGEND_ACTIVE]: legendActive },
        persistent: false,
      })
    );
  })
);

export const routines = collectRoutines(
  reactToLoadVisualizationSlide,
  applyLoadedSlideToView,
  applyToggledLegendToView,
  reactToLoadMetamodelSlide
);
